import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { ProfessionalDisciplinesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as ProfessionalDisciplineActions from "../../../actions/professionalDisciplines";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { LookupsLayout } from "../../index";

import "./ProfessionalDisciplinesPage.scss";

interface ProfessionalDisciplinesPageProps {
    formFilter: IFormFilter;
    professionalDiscipline: Dtos.ProfessionalDiscipline[];
    loadingProfessionalDisciplines: boolean;
    loadProfessionalDisciplinesSuccess: boolean;
    loadProfessionalDisciplinesFailure: boolean;
    loadProfessionalDisciplinesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearProfessionalDisciplines: ProfessionalDisciplineActions.IProfessionalDisciplineClearActionCreator;
    loadProfessionalDisciplines: ProfessionalDisciplineActions.IProfessionalDisciplineLoadActionCreator;
    filter: ProfessionalDisciplineActions.IProfessionalDisciplineFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const ProfessionalDisciplinesPage: FC<ProfessionalDisciplinesPageProps> = ({
    formFilter,
    professionalDiscipline,
    loadingProfessionalDisciplines,
    loadProfessionalDisciplinesSuccess,
    loadProfessionalDisciplinesFailure,
    loadProfessionalDisciplinesErrorCode,
    permissions,
    clearProfessionalDisciplines,
    loadProfessionalDisciplines,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showProfessionalDisciplines =
        !loadingProfessionalDisciplines && loadProfessionalDisciplinesSuccess;


    // Load professional disciplines
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(ProfessionalDisciplinesBreadcrumbs());

            loadProfessionalDisciplines();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        ProfessionalDisciplinesBreadcrumbs,
        loadProfessionalDisciplines,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProfessionalDisciplinesFailure) {
            navigate(`/error/${loadProfessionalDisciplinesErrorCode}`);
        }
    }, [
        loadProfessionalDisciplinesFailure,
        loadProfessionalDisciplinesErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the profession disciplines on component unmount
    useEffect(() => {
        return () => {
            clearProfessionalDisciplines();
        };
    }, [clearProfessionalDisciplines]);

    const createTable = useMemo(() => {
        if (!professionalDiscipline || professionalDiscipline.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no professional disciplines to
                            view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination =
            professionalDiscipline.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={professionalDiscipline}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? DEFAULT_PAGE_SIZE
                            : professionalDiscipline.length
                    }
                    pageSize={
                        showPagination
                            ? undefined
                            : professionalDiscipline.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no professionalDiscipline to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/general/professionaldisciplines/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        professionalDiscipline,
        DEFAULT_PAGE_SIZE,
        PAGE_SIZE_OPTIONS,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Professional Disciplines</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.ProfessionalDisciplinesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/general/professionaldisciplines/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showProfessionalDisciplines ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showProfessionalDisciplines ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showProfessionalDisciplines,
        createTable,
    ]);

    return (
        <LookupsLayout
            loading={loadingProfessionalDisciplines}
            permission={Dtos.Permission.ProfessionalDisciplinesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<ProfessionalDisciplinesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.professionalDisciplines.formFilter,
        professionalDiscipline: state.professionalDisciplines.formFilteredData,
        loadingProfessionalDisciplines:
            state.professionalDisciplines.loadState &&
            state.professionalDisciplines.loadState.status ==
                RequestState.Pending,
        loadProfessionalDisciplinesSuccess:
            state.professionalDisciplines.loadState &&
            state.professionalDisciplines.loadState.status ==
                RequestState.Success,
        loadProfessionalDisciplinesFailure:
            state.professionalDisciplines.loadState &&
            state.professionalDisciplines.loadState.status ==
                RequestState.Failure,
        loadProfessionalDisciplinesErrorCode:
            state.professionalDisciplines.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<ProfessionalDisciplinesPageProps> => {
    return {
        loadProfessionalDisciplines: bindActionCreators(
            ProfessionalDisciplineActions.LoadProfessionalDiscipline,
            dispatch
        ),
        clearProfessionalDisciplines: bindActionCreators(
            ProfessionalDisciplineActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(
            ProfessionalDisciplineActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ProfessionalDisciplinesPage);
